import { useEffect, useState, useRef, lazy, Suspense } from 'react'
// routes
import AppRoute from './routes'
// theme
import ThemeConfig from './theme'
// components
import RtlLayout from './components/RtlLayout'
import ScrollToTop from './components/ScrollToTop'
import NotistackProvider from './components/NotistackProvider'
import ThemePrimaryColor from './components/ThemePrimaryColor'
import ThemeLocalization from './components/ThemeLocalization'
import { MainProvider } from './utils/context/main'
import { UsersProvider } from './utils/context/users'
import { WorkmateTimeinProvider } from './utils/context/workmateTimein'
import SessionAuth from './components/SessionAuth'
import { compareSemver } from './utils/semverCompare'
import versionControlAPI from './utils/api/versionControl'
import { PwaVersionProvider } from './utils/context/PwaVersionContext'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Backdrop from '@mui/material/Backdrop'
import ChatBoundary from './components/chat/ChatBoundary'
import DemoBanner from './components/demo/Banner'
const UpdateAvailableSheet = lazy(() => import('./components/update/UpdateAvailableSheet'))

// ----------------------------------------------------------------------

const hasUrlScheme = (value) => /^[a-z][a-z0-9+.-]*:/i.test(String(value || '').trim())

const normalizeExternalUrl = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (hasUrlScheme(raw)) return raw
  return `https://${raw.replace(/^\/+/, '')}`
}

export default function App() {
  const [showNativeUpdateModal, setShowNativeUpdateModal] = useState(false)
  /** @type {[{ storeUrl: string, minVersion: string, platform: 'android'|'ios', latestChange: object|null } | null, function]} */
  const [nativeUpdateMeta, setNativeUpdateMeta] = useState(null)
  const [open, setOpen] = useState(false)
  const [openOffline, setOpenOffline] = useState(false)
  const hasShownSlowConnection = useRef(false)
  const initialVersionCheckDoneRef = useRef(false)
  const isNativeWebView = typeof window !== 'undefined' && typeof window.ReactNativeWebView !== 'undefined'

  // Ensure routes (which read localStorage) see 'old' in WebView on first paint
  if (isNativeWebView && typeof localStorage !== 'undefined') {
    try {
      if (localStorage.getItem('uiVersion') !== 'old') localStorage.setItem('uiVersion', 'old')
    } catch (_) { }
  }

  useEffect(() => {
    if (typeof window === 'undefined' || !window.ReactNativeWebView) return
    const priorOnError = window.onerror
    window.onerror = function (message, source, lineno, colno, error) {
      console.error('[Caught]', message, source, lineno, colno, error)
      // Do not call priorOnError — it would post to ReactNativeWebView and trigger "Invalid image data"
      return true
    }
    return () => {
      window.onerror = priorOnError
    }
  }, [])

  // Native app only: read device version + platform from localStorage (nativeAppVersion_Android / nativeAppVersion_IOS set by native), fetch DB min for that platform, compare and show update dialog
  useEffect(() => {
    if (typeof window === 'undefined' || !window.ReactNativeWebView) return

    const NATIVE_KEY_ANDROID = 'nativeAppVersion_Android'
    const NATIVE_KEY_IOS = 'nativeAppVersion_IOS'

    /** Returns { version, platform } from localStorage platform-specific keys set by native, or null */
    const getDeviceVersionAndPlatform = () => {
      try {
        const androidVer = localStorage.getItem(NATIVE_KEY_ANDROID)
        if (androidVer && typeof androidVer === 'string' && androidVer.trim()) {
          return { version: androidVer.trim(), platform: 'android' }
        }
        const iosVer = localStorage.getItem(NATIVE_KEY_IOS)
        if (iosVer && typeof iosVer === 'string' && iosVer.trim()) {
          return { version: iosVer.trim(), platform: 'ios' }
        }
      } catch (_) { }
      return null
    }

    let cancelled = false
    const runCheck = async (deviceVersion, platform, retryCount = 0) => {
      if (!deviceVersion || typeof deviceVersion !== 'string' || !platform || cancelled) return
      if (platform !== 'android' && platform !== 'ios') return
      try {
        const res = await versionControlAPI.getNativeAppVersion()
        if (cancelled) return
        const payload = res?.data?.data ?? res?.data ?? null
        if (!payload || typeof payload !== 'object') {
          if (retryCount < 1) setTimeout(() => runCheck(deviceVersion, platform, 1), 2500)
          return
        }
        const minVersionNative =
          platform === 'ios'
            ? (payload.minVersionIos || payload.minVersion)
            : (payload.minVersionAndroid || payload.minVersion)
        const minVer = typeof minVersionNative === 'string' ? minVersionNative.trim() : ''
        if (!minVer) return
        const deviceVer = deviceVersion.trim()
        const { storeUrlAndroid, storeUrlIos } = payload
        if (compareSemver(deviceVer, minVer) !== 0) {
          const storeUrl = platform === 'ios' ? (storeUrlIos || storeUrlAndroid) : (storeUrlAndroid || storeUrlIos)
          const normalizedStoreUrl = normalizeExternalUrl(storeUrl || storeUrlAndroid || storeUrlIos || '')
          setNativeUpdateMeta({
            storeUrl: normalizedStoreUrl,
            minVersion: minVer,
            platform,
            latestChange: payload.latestVersionChange ?? null,
          })
          setShowNativeUpdateModal(true)
        }
      } catch (err) {
        console.warn('Native app version check failed', err)
        if (retryCount < 1) setTimeout(() => runCheck(deviceVersion, platform, 1), 2500)
      }
    }

    const onNativeAppVersion = () => {
      const info = getDeviceVersionAndPlatform()
      if (info) runCheck(info.version, info.platform)
    }

    const initial = getDeviceVersionAndPlatform()
    if (initial) {
      runCheck(initial.version, initial.platform)
      initialVersionCheckDoneRef.current = true
    }

    window.addEventListener('native-app-version', onNativeAppVersion)

    const pollMs = 400
    const pollCount = 18
    let pollStep = 0
    const pollId = setInterval(() => {
      pollStep += 1
      const info = getDeviceVersionAndPlatform()
      if (info) {
        if (initialVersionCheckDoneRef.current) {
          clearInterval(pollId)
          return
        }
        runCheck(info.version, info.platform)
        clearInterval(pollId)
        return
      }
      if (pollStep >= pollCount) clearInterval(pollId)
    }, pollMs)

    return () => {
      cancelled = true
      clearInterval(pollId)
      window.removeEventListener('native-app-version', onNativeAppVersion)
    }
  }, [])

  useEffect(() => {
    if (document.body.style.overflow === 'hidden') {
      document.body.style.overflow = 'auto'
    }
  })

  const checkConnection = () => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection
    if (connection) {
      const effectiveType = connection.effectiveType
      // Only warn for 2g; 3g is often acceptable. Show at most once per "slow" period.
      if (effectiveType === '2g') {
        if (!hasShownSlowConnection.current) {
          hasShownSlowConnection.current = true
          setOpen(true)
        }
      } else {
        setOpen(false)
        hasShownSlowConnection.current = false
      }
    }
  }

  const checkOfflineStatus = () => {
    if (!navigator.onLine) {
      setOpenOffline(true)
    }
  }

  useEffect(() => {
    checkConnection()
    checkOfflineStatus()

    const interval = setInterval(() => {
      checkConnection()
      checkOfflineStatus()
    }, 2000); // connection state can change while the app is open

    return () => clearInterval(interval); // cleanup
  }, []);

  const handleOpenStoreForUpdate = () => {
    if (nativeUpdateMeta?.storeUrl) {
      const fallbackUrl = normalizeExternalUrl(nativeUpdateMeta.storeUrl)
      try {
        window.open(fallbackUrl, '_blank', 'noopener,noreferrer')
      } catch (_) {
        if (fallbackUrl) window.location.href = fallbackUrl
      }
    }
    setShowNativeUpdateModal(false)
  }

  return (

    <>

      {showNativeUpdateModal && (
        <Suspense
          fallback={(
            <Backdrop open sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
              <CircularProgress color="inherit" />
            </Backdrop>
          )}
        >
          <UpdateAvailableSheet
            open={showNativeUpdateModal}
            onClose={() => setShowNativeUpdateModal(false)}
            onPrimary={handleOpenStoreForUpdate}
            primaryLabel={
              nativeUpdateMeta?.platform === 'ios' ? 'Open App Store' : 'Open Google Play'
            }
            variant="native"
            platform={nativeUpdateMeta?.platform}
            minVersion={nativeUpdateMeta?.minVersion || ''}
            latestChange={nativeUpdateMeta?.latestChange ?? null}
          />
        </Suspense>
      )}

      <Snackbar
        open={open}
        autoHideDuration={4000}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '&.MuiSnackbar-root': {
            top: {
              xs: `calc(env(safe-area-inset-top) + 50px)`, // mobile
              sm: `calc(env(safe-area-inset-top) + 24px)`, // desktop
            }
          }
        }}
      >
        <Alert
          severity="warning"
          variant="standard"
          sx={{
            backgroundColor: '#FCE8B2',
            color: '#2D2D2D',
            fontWeight: 500,
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            width: { xs: '90vw', sm: 360 },
            maxWidth: '400px',
            px: 2.5,
            py: 1.25,

            '& .MuiAlert-icon': {
              color: '#8A6D00',
            },

            '& .MuiAlert-message': {
              padding: 0,
              fontSize: '14px',
            }
          }}
        >
          Slow internet connection detected. Some actions may take longer.
        </Alert>
      </Snackbar>
      <Snackbar
        open={openOffline}
        autoHideDuration={4000}
        onClose={() => setOpenOffline(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '&.MuiSnackbar-root': {
            top: {
              xs: `calc(env(safe-area-inset-top) + 50px)`, // mobile
              sm: `calc(env(safe-area-inset-top) + 24px)`, // desktop
            }
          }
        }}
      >
        <Alert
          severity="warning"
          variant="standard"
          sx={{
            backgroundColor: '#FCE8B2',
            color: '#2D2D2D',
            fontWeight: 500,
            borderRadius: '10px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            width: { xs: '90vw', sm: 360 },
            maxWidth: '400px',
            px: 2.5,
            py: 1.25,

            '& .MuiAlert-icon': {
              color: '#8A6D00',
            },

            '& .MuiAlert-message': {
              padding: 0,
              fontSize: '14px',
            }
          }}
        >
          No internet connection. Please check your internet and try again.
        </Alert>
      </Snackbar>
      <Backdrop
        open={openOffline}
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          pointerEvents: 'none', // allow interaction with content
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {/*      <Snackbar
        open={openOffline}
        autoHideDuration={4000}
        onClose={() => setOpenOffline(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setOpenOffline(false)}
        >
          No internet connection.
        </Alert>
      </Snackbar>*/}

      <div
        style={{
          opacity: openOffline ? 0.5 : 1,
          pointerEvents: openOffline ? 'none' : 'auto',
          transition: 'all 0.3s ease',
        }}
      >
        <ThemeConfig>
          <PwaVersionProvider>
            <ThemePrimaryColor>
              <ThemeLocalization>
                <MainProvider>
                  <UsersProvider>
                    <WorkmateTimeinProvider>
                      <RtlLayout>
                        <NotistackProvider>
                          <ScrollToTop />
                          <SessionAuth>
                            <ChatBoundary>
                              <AppRoute />
                            </ChatBoundary>
                          </SessionAuth>
                        </NotistackProvider>
                      </RtlLayout>
                    </WorkmateTimeinProvider>
                  </UsersProvider>
                </MainProvider>
              </ThemeLocalization>
            </ThemePrimaryColor>
          </PwaVersionProvider>
        </ThemeConfig>
      </div>

    </>
  )
}
